import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Copy, CheckCircle, XCircle, AlertCircle, Smartphone, Globe, Wifi, Shield } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function DiagnosticsPage() {
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(true);
  const [testLogs, setTestLogs] = useState([]);

  const addLog = (message, type = "info") => {
    const timestamp = new Date().toLocaleTimeString();
    setTestLogs(prev => [...prev, { timestamp, message, type }]);
  };

  useEffect(() => {
    runDiagnostics();
  }, []);

  const runDiagnostics = async () => {
    setLoading(true);
    setTestLogs([]);
    const diagnostics = {};

    // 1. Device Info
    addLog("Coletando informações do dispositivo...");
    const ua = navigator.userAgent;
    diagnostics.userAgent = ua;
    diagnostics.isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    diagnostics.isIPadOS = ua.includes('Mac') && 'ontouchend' in document;
    diagnostics.isSafari = /Safari/.test(ua) && !/Chrome|CriOS|FxiOS|EdgiOS/.test(ua);
    diagnostics.isChrome = /CriOS/.test(ua);
    diagnostics.isFirefox = /FxiOS/.test(ua);
    
    // Extract iOS version
    const iosMatch = ua.match(/OS (\d+)_(\d+)/);
    diagnostics.iosVersion = iosMatch ? `${iosMatch[1]}.${iosMatch[2]}` : "N/A";
    
    addLog(`iOS: ${diagnostics.isIOS || diagnostics.isIPadOS}, Versão: ${diagnostics.iosVersion}`, 
           diagnostics.isIOS ? "warning" : "success");

    // 2. LocalStorage Test
    addLog("Testando localStorage...");
    try {
      localStorage.setItem("test_key", "test_value");
      const retrieved = localStorage.getItem("test_key");
      localStorage.removeItem("test_key");
      diagnostics.localStorageWorks = retrieved === "test_value";
      addLog(`localStorage: ${diagnostics.localStorageWorks ? "OK" : "BLOQUEADO"}`, 
             diagnostics.localStorageWorks ? "success" : "error");
    } catch (e) {
      diagnostics.localStorageWorks = false;
      diagnostics.localStorageError = e.message;
      addLog(`localStorage: ERRO - ${e.message}`, "error");
    }

    // 3. Cookies Test
    addLog("Testando cookies...");
    try {
      document.cookie = "test_cookie=test_value; SameSite=None; Secure";
      diagnostics.cookiesEnabled = document.cookie.includes("test_cookie");
      addLog(`Cookies: ${diagnostics.cookiesEnabled ? "OK" : "BLOQUEADO"}`,
             diagnostics.cookiesEnabled ? "success" : "error");
    } catch (e) {
      diagnostics.cookiesEnabled = false;
      addLog(`Cookies: ERRO - ${e.message}`, "error");
    }

    // 4. Fetch API Test (to backend)
    addLog("Testando conexão com backend (fetch)...");
    try {
      const startTime = Date.now();
      const response = await fetch(`${BACKEND_URL}/api/health`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
      });
      const elapsed = Date.now() - startTime;
      diagnostics.fetchWorks = response.ok;
      diagnostics.fetchStatus = response.status;
      diagnostics.fetchTime = elapsed;
      addLog(`Fetch GET: ${response.ok ? "OK" : "FALHOU"} (${response.status}) - ${elapsed}ms`,
             response.ok ? "success" : "error");
    } catch (e) {
      diagnostics.fetchWorks = false;
      diagnostics.fetchError = e.message;
      addLog(`Fetch GET: ERRO - ${e.message}`, "error");
    }

    // 5. Fetch POST Test (without credentials - iOS Safari 18+ compatible)
    addLog("Testando POST com fetch (sem credentials)...");
    try {
      const startTime = Date.now();
      const response = await fetch(`${BACKEND_URL}/api/auth/guest`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({}),
        mode: 'cors',
      });
      const elapsed = Date.now() - startTime;
      const data = await response.json().catch(() => ({}));
      diagnostics.fetchPostWorks = response.ok;
      diagnostics.fetchPostStatus = response.status;
      diagnostics.fetchPostTime = elapsed;
      diagnostics.fetchPostHasToken = !!data.access_token;
      addLog(`Fetch POST: ${response.ok ? "OK" : "FALHOU"} (${response.status}) - ${elapsed}ms`,
             response.ok ? "success" : "error");
      if (data.access_token) {
        addLog("Token recebido: SIM", "success");
      }
      if (data.detail) {
        addLog(`Resposta: ${data.detail}`, "warning");
      }
    } catch (e) {
      diagnostics.fetchPostWorks = false;
      diagnostics.fetchPostError = e.message;
      addLog(`Fetch POST: ERRO - ${e.message}`, "error");
    }

    // 6. CORS Preflight Test
    addLog("Testando CORS preflight...");
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'OPTIONS',
        headers: {
          'Origin': window.location.origin,
          'Access-Control-Request-Method': 'POST',
          'Access-Control-Request-Headers': 'content-type',
        },
      });
      diagnostics.corsPreflightWorks = response.ok || response.status === 204;
      diagnostics.corsPreflightStatus = response.status;
      addLog(`CORS Preflight: ${diagnostics.corsPreflightWorks ? "OK" : "FALHOU"} (${response.status})`,
             diagnostics.corsPreflightWorks ? "success" : "error");
    } catch (e) {
      diagnostics.corsPreflightWorks = false;
      diagnostics.corsPreflightError = e.message;
      addLog(`CORS Preflight: ERRO - ${e.message}`, "error");
    }

    // 7. WebSocket Test (if applicable)
    addLog("Verificando suporte a features...");
    diagnostics.supportsWebSocket = 'WebSocket' in window;
    diagnostics.supportsServiceWorker = 'serviceWorker' in navigator;
    diagnostics.supportsFetch = 'fetch' in window;
    addLog(`WebSocket: ${diagnostics.supportsWebSocket ? "OK" : "NÃO"}`);
    addLog(`ServiceWorker: ${diagnostics.supportsServiceWorker ? "OK" : "NÃO"}`);
    addLog(`Fetch API: ${diagnostics.supportsFetch ? "OK" : "NÃO"}`);

    // 8. Check third-party cookie settings
    addLog("Verificando configurações de privacidade...");
    diagnostics.doNotTrack = navigator.doNotTrack === "1";
    diagnostics.cookieEnabled = navigator.cookieEnabled;
    addLog(`Do Not Track: ${diagnostics.doNotTrack ? "ATIVO" : "Não"}`);
    addLog(`navigator.cookieEnabled: ${diagnostics.cookieEnabled ? "SIM" : "NÃO"}`,
           diagnostics.cookieEnabled ? "success" : "error");

    // 9. Test localStorage token save/retrieve
    addLog("Testando salvamento de token...");
    try {
      const testToken = "test_token_" + Date.now();
      localStorage.setItem("auth_token_backup", testToken);
      const retrieved = localStorage.getItem("auth_token_backup");
      diagnostics.tokenSaveWorks = retrieved === testToken;
      localStorage.removeItem("auth_token_backup");
      addLog(`Salvar/Ler token: ${diagnostics.tokenSaveWorks ? "OK" : "FALHOU"}`,
             diagnostics.tokenSaveWorks ? "success" : "error");
    } catch (e) {
      diagnostics.tokenSaveWorks = false;
      addLog(`Salvar/Ler token: ERRO - ${e.message}`, "error");
    }

    setResults(diagnostics);
    setLoading(false);
    addLog("Diagnóstico completo!", "success");
  };

  const copyResults = () => {
    const text = JSON.stringify(results, null, 2);
    navigator.clipboard.writeText(text);
    toast.success("Resultados copiados!");
  };

  const copyLogs = () => {
    const text = testLogs.map(l => `[${l.timestamp}] ${l.type.toUpperCase()}: ${l.message}`).join('\n');
    navigator.clipboard.writeText(text);
    toast.success("Logs copiados!");
  };

  const getStatusIcon = (success) => {
    if (success === true) return <CheckCircle className="w-5 h-5 text-green-500" />;
    if (success === false) return <XCircle className="w-5 h-5 text-red-500" />;
    return <AlertCircle className="w-5 h-5 text-yellow-500" />;
  };

  return (
    <div className="min-h-screen bg-zinc-950 p-4 sm:p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
            Diagnóstico de Conexão
          </h1>
          <p className="text-zinc-400">
            Esta página testa a compatibilidade do seu dispositivo com o app
          </p>
        </div>

        {/* Device Info Card */}
        <Card className="bg-zinc-900 border-zinc-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Smartphone className="w-5 h-5" />
              Informações do Dispositivo
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2">
                {getStatusIcon(!results.isIOS && !results.isIPadOS)}
                <span className="text-zinc-400">iOS:</span>
                <span className="text-white font-mono">
                  {results.isIOS || results.isIPadOS ? `SIM (v${results.iosVersion})` : "NÃO"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-zinc-500" />
                <span className="text-zinc-400">Navegador:</span>
                <span className="text-white font-mono">
                  {results.isSafari ? "Safari" : results.isChrome ? "Chrome" : results.isFirefox ? "Firefox" : "Outro"}
                </span>
              </div>
            </div>
            <div className="bg-zinc-800 p-3 rounded-lg">
              <p className="text-xs text-zinc-500 mb-1">User Agent:</p>
              <p className="text-xs text-zinc-300 break-all font-mono">{results.userAgent}</p>
            </div>
          </CardContent>
        </Card>

        {/* Connection Tests Card */}
        <Card className="bg-zinc-900 border-zinc-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Wifi className="w-5 h-5" />
              Testes de Conexão
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-center gap-2 p-3 bg-zinc-800 rounded-lg">
                {getStatusIcon(results.localStorageWorks)}
                <span className="text-zinc-300">LocalStorage</span>
              </div>
              <div className="flex items-center gap-2 p-3 bg-zinc-800 rounded-lg">
                {getStatusIcon(results.cookiesEnabled)}
                <span className="text-zinc-300">Cookies</span>
              </div>
              <div className="flex items-center gap-2 p-3 bg-zinc-800 rounded-lg">
                {getStatusIcon(results.fetchWorks)}
                <span className="text-zinc-300">Fetch GET</span>
                {results.fetchTime && <span className="text-xs text-zinc-500">({results.fetchTime}ms)</span>}
              </div>
              <div className="flex items-center gap-2 p-3 bg-zinc-800 rounded-lg">
                {getStatusIcon(results.fetchPostWorks)}
                <span className="text-zinc-300">Fetch POST</span>
                {results.fetchPostTime && <span className="text-xs text-zinc-500">({results.fetchPostTime}ms)</span>}
              </div>
              <div className="flex items-center gap-2 p-3 bg-zinc-800 rounded-lg">
                {getStatusIcon(results.corsPreflightWorks)}
                <span className="text-zinc-300">CORS Preflight</span>
              </div>
              <div className="flex items-center gap-2 p-3 bg-zinc-800 rounded-lg">
                {getStatusIcon(results.cookieEnabled)}
                <span className="text-zinc-300">navigator.cookieEnabled</span>
              </div>
            </div>

            {results.fetchPostError && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-red-400 text-sm font-medium">Erro no POST:</p>
                <p className="text-red-300 text-xs font-mono">{results.fetchPostError}</p>
              </div>
            )}

            {results.fetchError && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-red-400 text-sm font-medium">Erro no GET:</p>
                <p className="text-red-300 text-xs font-mono">{results.fetchError}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Test Logs Card */}
        <Card className="bg-zinc-900 border-zinc-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Log de Testes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-black rounded-lg p-3 max-h-64 overflow-y-auto font-mono text-xs space-y-1">
              {testLogs.map((log, idx) => (
                <div key={idx} className={`
                  ${log.type === 'error' ? 'text-red-400' : ''}
                  ${log.type === 'success' ? 'text-green-400' : ''}
                  ${log.type === 'warning' ? 'text-yellow-400' : ''}
                  ${log.type === 'info' ? 'text-zinc-400' : ''}
                `}>
                  <span className="text-zinc-600">[{log.timestamp}]</span> {log.message}
                </div>
              ))}
              {loading && <div className="text-blue-400 animate-pulse">Executando testes...</div>}
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={runDiagnostics}
            disabled={loading}
            className="flex-1 bg-purple-600 hover:bg-purple-700"
          >
            {loading ? "Executando..." : "Executar Novamente"}
          </Button>
          <Button
            onClick={copyLogs}
            variant="outline"
            className="flex-1 border-zinc-700 text-white"
          >
            <Copy className="w-4 h-4 mr-2" />
            Copiar Logs
          </Button>
          <Button
            onClick={copyResults}
            variant="outline"
            className="flex-1 border-zinc-700 text-white"
          >
            <Copy className="w-4 h-4 mr-2" />
            Copiar JSON
          </Button>
        </div>

        {/* Instructions */}
        <Card className="bg-blue-500/10 border-blue-500/30">
          <CardContent className="pt-6">
            <h3 className="text-blue-400 font-semibold mb-2">Instruções:</h3>
            <ol className="text-blue-300 text-sm space-y-1 list-decimal list-inside">
              <li>Execute os testes nesta página</li>
              <li>Clique em "Copiar Logs"</li>
              <li>Envie os logs para o desenvolvedor</li>
              <li>Isso ajudará a identificar o problema no seu dispositivo</li>
            </ol>
          </CardContent>
        </Card>

        {/* Back link */}
        <div className="text-center">
          <a href="/auth" className="text-purple-400 hover:text-purple-300 text-sm">
            ← Voltar para Login
          </a>
        </div>
      </div>
    </div>
  );
}
